import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X, UserCheck, User, AlertTriangle } from "lucide-react";
import toast from "react-hot-toast";
import { getTeacherById } from "../../services/teachers";

interface StatusChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  teacherId: string;
}

const StatusChangeModal: React.FC<StatusChangeModalProps> = ({
  isOpen,
  onClose,
  teacherId,
}) => {
  const queryClient = useQueryClient();
  const [newStatus, setNewStatus] = useState<
    "active" | "inactive" | "suspended" | "pending"
  >("active");
  const [reason, setReason] = useState("");

  // Fetch teacher data
  const { data: teacher } = useQuery({
    queryKey: ["teacher", teacherId],
    queryFn: () => getTeacherById(teacherId),
    enabled: isOpen && !!teacherId,
  });

  // Change status mutation
  const changeStatusMutation = useMutation({
    mutationFn: async (data: {
      status: "active" | "inactive" | "suspended" | "pending";
      reason: string;
    }) => {
      // Get the user ID from teacher data - check both possible structures
      const teacherData = teacher?.data as any;
      let userId = null;

      // First try userInfo (from aggregated queries)
      if (teacherData?.userInfo?._id) {
        userId = teacherData.userInfo._id;
      }
      // Then try userId field (from populated single queries)
      else if (
        typeof teacherData?.userId === "object" &&
        teacherData?.userId?._id
      ) {
        userId = teacherData.userId._id;
      }
      // Finally try userId as string
      else if (typeof teacherData?.userId === "string") {
        userId = teacherData.userId;
      }

      if (!userId) {
        console.error("Teacher data structure:", teacherData);
        throw new Error("User ID not found in teacher data");
      }

      const API_URL =
        import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";
      const response = await fetch(
        `${API_URL}/api/auth/users/${userId}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            status: data.status,
            reason: data.reason,
          }),
        }
      );
      if (!response.ok) throw new Error("Failed to update teacher status");
      return response.json();
    },
    onSuccess: () => {
      toast.success("Teacher status updated successfully");
      queryClient.invalidateQueries({ queryKey: ["teachers"] });
      queryClient.invalidateQueries({ queryKey: ["teacher", teacherId] });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update status");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      toast.error("Please provide a reason for status change");
      return;
    }
    changeStatusMutation.mutate({ status: newStatus, reason });
  };

  React.useEffect(() => {
    if (isOpen && teacher?.data) {
      const teacherData = teacher.data as any;
      const status =
        teacherData.status || (teacherData.isActive ? "active" : "inactive");
      setNewStatus(status as "active" | "inactive" | "suspended" | "pending");
      setReason("");
    }
  }, [isOpen, teacher]);

  if (!isOpen) return null;

  const teacherData = teacher?.data as any;
  // Get current status - check for explicit status field first, then fallback to isActive
  const currentStatus =
    teacherData?.status || (teacherData?.isActive ? "active" : "inactive");

  // Get teacher name from either userInfo or userId fields
  let firstName = "";
  let lastName = "";

  if (teacherData?.userInfo) {
    firstName = teacherData.userInfo.firstName || "";
    lastName = teacherData.userInfo.lastName || "";
  } else if (typeof teacherData?.userId === "object") {
    firstName = teacherData.userId.firstName || "";
    lastName = teacherData.userId.lastName || "";
  }

  // Helper function to get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "inactive":
        return "bg-red-100 text-red-800";
      case "suspended":
        return "bg-orange-100 text-orange-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Helper function to get status display text
  const getStatusText = (status: string) => {
    switch (status) {
      case "active":
        return "Active";
      case "inactive":
        return "Inactive";
      case "suspended":
        return "Suspended";
      case "pending":
        return "Pending";
      default:
        return "Unknown";
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <UserCheck className="h-6 w-6 text-green-600" />
            Change Teacher Status
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Teacher Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">
                  {firstName} {lastName}
                </h3>
                <p className="text-sm text-gray-600">
                  Current Status:
                  <span
                    className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                      currentStatus
                    )}`}
                  >
                    {getStatusText(currentStatus)}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Status Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              New Status
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="status"
                  value="active"
                  checked={newStatus === "active"}
                  onChange={(e) =>
                    setNewStatus(
                      e.target.value as
                        | "active"
                        | "inactive"
                        | "suspended"
                        | "pending"
                    )
                  }
                  className="text-green-600"
                />
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 bg-green-500 rounded-full"></div>
                  <span className="font-medium">Active</span>
                </div>
                <span className="text-sm text-gray-500 ml-auto">
                  Full system access
                </span>
              </label>

              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="status"
                  value="inactive"
                  checked={newStatus === "inactive"}
                  onChange={(e) =>
                    setNewStatus(
                      e.target.value as
                        | "active"
                        | "inactive"
                        | "suspended"
                        | "pending"
                    )
                  }
                  className="text-red-600"
                />
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 bg-red-500 rounded-full"></div>
                  <span className="font-medium">Inactive</span>
                </div>
                <span className="text-sm text-gray-500 ml-auto">
                  Account disabled
                </span>
              </label>

              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="status"
                  value="suspended"
                  checked={newStatus === "suspended"}
                  onChange={(e) =>
                    setNewStatus(
                      e.target.value as
                        | "active"
                        | "inactive"
                        | "suspended"
                        | "pending"
                    )
                  }
                  className="text-orange-600"
                />
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 bg-orange-500 rounded-full"></div>
                  <span className="font-medium">Suspended</span>
                </div>
                <span className="text-sm text-gray-500 ml-auto">
                  Temporarily restricted
                </span>
              </label>

              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="status"
                  value="pending"
                  checked={newStatus === "pending"}
                  onChange={(e) =>
                    setNewStatus(
                      e.target.value as
                        | "active"
                        | "inactive"
                        | "suspended"
                        | "pending"
                    )
                  }
                  className="text-yellow-600"
                />
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 bg-yellow-500 rounded-full"></div>
                  <span className="font-medium">Pending</span>
                </div>
                <span className="text-sm text-gray-500 ml-auto">
                  Awaiting approval
                </span>
              </label>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Status Change *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Please provide a reason for this status change..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {newStatus !== currentStatus && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-yellow-800">
                    Status Change Warning
                  </h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    {newStatus === "inactive" &&
                      "This teacher will lose access to the system and cannot log in until reactivated."}
                    {newStatus === "active" &&
                      "This teacher will regain full access to the system and can log in normally."}
                    {newStatus === "suspended" &&
                      "This teacher will have restricted access and may not be able to perform certain actions."}
                    {newStatus === "pending" &&
                      "This teacher's account will require approval before they can access the system."}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                changeStatusMutation.isPending || newStatus === currentStatus
              }
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {changeStatusMutation.isPending ? "Updating..." : "Update Status"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StatusChangeModal;
